/* IPERFORMANCE — progressive enhancement layer (vanilla, no dependencies).
   Everything is scoped, idempotent and re-initialised on Theme Editor section loads. */
(() => {
  'use strict';

  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduceMotion.matches) root.classList.add('ip-reduced');
  if (window.Shopify && window.Shopify.designMode) root.classList.add('ip-design-mode');

  /* ---------- Reveal on scroll ---------- */
  let revealObserver = null;
  const getRevealObserver = () => {
    if (revealObserver || !('IntersectionObserver' in window)) return revealObserver;
    revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        revealObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    return revealObserver;
  };

  const initReveal = (scope) => {
    const items = scope.querySelectorAll('.ip-anim [data-ip-reveal]:not(.is-in)');
    const io = getRevealObserver();
    if (!io || reduceMotion.matches) { items.forEach((el) => el.classList.add('is-in')); return; }
    items.forEach((el) => io.observe(el));
  };

  /* ---------- Hero parallax (transform only, rAF throttled) ---------- */
  const parallaxEls = new Set();
  let ticking = false;
  const updateParallax = () => {
    ticking = false;
    const vh = window.innerHeight;
    parallaxEls.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > vh) return;
      const offset = Math.max(-80, Math.min(80, rect.top * -0.12));
      el.style.setProperty('--ip-parallax', `${offset.toFixed(1)}px`);
    });
  };
  const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(updateParallax); } };
  const initParallax = (scope) => {
    if (reduceMotion.matches) return;
    scope.querySelectorAll('.ip-hero__media--parallax').forEach((el) => parallaxEls.add(el));
    if (parallaxEls.size) { window.addEventListener('scroll', onScroll, { passive: true }); updateParallax(); }
  };

  /* ---------- Statement: words light up while scrolling ---------- */
  const statements = new Set();
  const updateStatements = () => {
    const vh = window.innerHeight;
    statements.forEach((el) => {
      const words = el.__ipWords;
      if (!words) return;
      const rect = el.getBoundingClientRect();
      const start = vh * 0.85;
      const end = vh * 0.35;
      const progress = Math.min(1, Math.max(0, (start - rect.top) / (start - end + rect.height * 0.6)));
      const lit = Math.round(progress * words.length);
      words.forEach((w, i) => w.classList.toggle('is-lit', i < lit));
    });
  };
  const initStatements = (scope) => {
    scope.querySelectorAll('[data-ip-words]').forEach((el) => {
      if (el.__ipWords) return;
      if (reduceMotion.matches) return;
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      const textNodes = [];
      while (walker.nextNode()) textNodes.push(walker.currentNode);
      textNodes.forEach((node) => {
        const frag = document.createDocumentFragment();
        node.textContent.split(/(\s+)/).forEach((part) => {
          if (!part) return;
          if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
          const span = document.createElement('span');
          span.className = 'ip-word';
          span.textContent = part;
          frag.appendChild(span);
        });
        node.parentNode.replaceChild(frag, node);
      });
      el.__ipWords = Array.from(el.querySelectorAll('.ip-word'));
      statements.add(el);
    });
    if (statements.size) {
      window.addEventListener('scroll', () => requestAnimationFrame(updateStatements), { passive: true });
      updateStatements();
    }
  };

  /* ---------- Rail navigation ---------- */
  class IpRailNav extends HTMLElement {
    connectedCallback() {
      this.rail = document.getElementById(this.getAttribute('for'));
      if (!this.rail) return;
      this.prev = this.querySelector('[data-dir="prev"]');
      this.next = this.querySelector('[data-dir="next"]');
      this.prev?.addEventListener('click', () => this.go(-1));
      this.next?.addEventListener('click', () => this.go(1));
      this.update = this.update.bind(this);
      this.rail.addEventListener('scroll', this.update, { passive: true });
      this.update();
    }
    go(dir) {
      const amount = this.rail.clientWidth * 0.85;
      this.rail.scrollBy({ left: dir * amount, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }
    update() {
      if (!this.rail) return;
      const max = this.rail.scrollWidth - this.rail.clientWidth - 2;
      if (this.prev) this.prev.disabled = this.rail.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.rail.scrollLeft >= max;
    }
  }
  if (!customElements.get('ip-rail-nav')) customElements.define('ip-rail-nav', IpRailNav);

  /* ---------- Tabs (WAI-ARIA tabs pattern) ---------- */
  class IpTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => {
          const key = e.key;
          let idx = null;
          if (key === 'ArrowRight') idx = (i + 1) % this.tabs.length;
          if (key === 'ArrowLeft') idx = (i - 1 + this.tabs.length) % this.tabs.length;
          if (key === 'Home') idx = 0;
          if (key === 'End') idx = this.tabs.length - 1;
          if (idx !== null) { e.preventDefault(); this.select(idx, true); }
        });
      });
    }
    select(index, focus = false) {
      this.tabs.forEach((tab, i) => {
        const on = i === index;
        tab.setAttribute('aria-selected', String(on));
        tab.tabIndex = on ? 0 : -1;
        const panel = document.getElementById(tab.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
        if (on && focus) tab.focus();
      });
    }
  }
  if (!customElements.get('ip-tabs')) customElements.define('ip-tabs', IpTabs);

  /* ---------- Dialogs (ingredient details etc.) ---------- */
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-ip-dialog]');
    if (opener) {
      const dialog = document.getElementById(opener.getAttribute('data-ip-dialog'));
      if (dialog && typeof dialog.showModal === 'function') {
        e.preventDefault();
        dialog.showModal();
        dialog.__ipOpener = opener;
      }
      return;
    }
    const closer = e.target.closest('[data-ip-dialog-close]');
    if (closer) { closer.closest('dialog')?.close(); return; }
    if (e.target.tagName === 'DIALOG' && e.target.classList.contains('ip-dialog')) e.target.close();
  });
  document.addEventListener('close', (e) => {
    if (e.target.classList?.contains('ip-dialog') && e.target.__ipOpener) e.target.__ipOpener.focus();
  }, true);

  /* ---------- Supplement finder ---------- */
  class IpFinder extends HTMLElement {
    connectedCallback() {
      if (this.__ready) return;
      this.__ready = true;
      this.steps = Array.from(this.querySelectorAll('[data-step]'));
      this.results = Array.from(this.querySelectorAll('[data-result]'));
      this.bars = Array.from(this.querySelectorAll('.ip-finder__progress span'));
      this.status = this.querySelector('[data-finder-status]');
      this.answers = [];
      this.index = 0;
      this.addEventListener('click', (e) => {
        const opt = e.target.closest('[data-tags]');
        if (opt) return this.choose(opt);
        if (e.target.closest('[data-finder-back]')) return this.back();
        if (e.target.closest('[data-finder-restart]')) return this.restart();
      });
      this.show(0);
    }
    choose(opt) {
      const step = opt.closest('[data-step]');
      step.querySelectorAll('[data-tags]').forEach((b) => b.setAttribute('aria-pressed', String(b === opt)));
      this.answers[this.index] = (opt.dataset.tags || '').split(',').map((t) => t.trim().toLowerCase()).filter(Boolean);
      window.setTimeout(() => (this.index < this.steps.length - 1 ? this.show(this.index + 1) : this.finish()), reduceMotion.matches ? 0 : 180);
    }
    back() { if (this.index > 0) this.show(this.index - 1); }
    restart() {
      this.answers = [];
      this.querySelectorAll('[aria-pressed="true"]').forEach((b) => b.setAttribute('aria-pressed', 'false'));
      this.show(0);
    }
    show(i) {
      this.index = i;
      this.results.forEach((r) => r.classList.remove('is-active'));
      this.steps.forEach((s, n) => s.classList.toggle('is-active', n === i));
      this.bars.forEach((b, n) => b.classList.toggle('is-done', n <= i));
      const back = this.querySelector('[data-finder-back]');
      if (back) back.hidden = i === 0;
      const nav = this.querySelector('.ip-finder__nav');
      if (nav) nav.hidden = false;
      const q = this.steps[i]?.querySelector('.ip-finder__q');
      if (this.status && q) this.status.textContent = q.textContent.trim();
      if (this.__started) this.steps[i]?.querySelector('[data-tags]')?.focus({ preventScroll: true });
      this.__started = true;
    }
    finish() {
      const weights = {};
      this.answers.flat().forEach((tag) => { weights[tag] = (weights[tag] || 0) + 1; });
      let best = null;
      let bestScore = -1;
      this.results.forEach((r) => {
        const tags = (r.dataset.result || '').split(',').map((t) => t.trim().toLowerCase()).filter(Boolean);
        const score = tags.reduce((sum, t) => sum + (weights[t] || 0), 0);
        if (score > bestScore) { best = r; bestScore = score; }
      });
      this.steps.forEach((s) => s.classList.remove('is-active'));
      this.bars.forEach((b) => b.classList.add('is-done'));
      const nav = this.querySelector('.ip-finder__nav');
      if (nav) nav.hidden = true;
      if (best) {
        best.classList.add('is-active');
        const heading = best.querySelector('[data-result-title]');
        if (this.status && heading) this.status.textContent = heading.textContent.trim();
        heading?.focus({ preventScroll: true });
        best.scrollIntoView({ block: 'nearest', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      }
    }
  }
  if (!customElements.get('ip-finder')) customElements.define('ip-finder', IpFinder);

  /* ---------- Ingredient library filter ---------- */
  class IpFilter extends HTMLElement {
    connectedCallback() {
      this.chips = Array.from(this.querySelectorAll('[data-filter]'));
      this.items = Array.from(this.querySelectorAll('[data-cat]'));
      this.chips.forEach((chip) => chip.addEventListener('click', () => {
        const value = chip.dataset.filter;
        this.chips.forEach((c) => c.setAttribute('aria-pressed', String(c === chip)));
        this.items.forEach((item) => {
          item.hidden = value !== 'all' && !item.dataset.cat.split(',').includes(value);
        });
      }));
    }
  }
  if (!customElements.get('ip-filter')) customElements.define('ip-filter', IpFilter);

  /* ---------- Scroll spy for in-page navigation (PDP nav, guide TOC) ---------- */
  const initSpy = (scope) => {
    scope.querySelectorAll('[data-ip-spy]').forEach((nav) => {
      if (nav.__ipSpy || !('IntersectionObserver' in window)) return;
      nav.__ipSpy = true;
      const links = Array.from(nav.querySelectorAll('a[href^="#"]'));
      const map = new Map();
      links.forEach((a) => {
        const target = document.getElementById(decodeURIComponent(a.hash.slice(1)));
        if (target) map.set(target, a);
        else if (nav.hasAttribute('data-ip-hide-missing')) a.hidden = true;
      });
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          links.forEach((l) => { l.classList.remove('is-active'); l.removeAttribute('aria-current'); });
          const link = map.get(entry.target);
          if (link) { link.classList.add('is-active'); link.setAttribute('aria-current', 'true'); }
        });
      }, { rootMargin: '-35% 0px -60% 0px' });
      map.forEach((_, target) => io.observe(target));
    });
  };

  /* ---------- Init / Theme editor ---------- */
  const init = (scope = document) => {
    initReveal(scope);
    initParallax(scope);
    initStatements(scope);
    initSpy(scope);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    e.target.closest('[data-ip-reveal]')?.classList.add('is-in');
    e.target.querySelectorAll?.('[data-ip-reveal]').forEach((el) => el.classList.add('is-in'));
  });
})();
